import { ActivatedRoute, Router } from '@angular/router';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, firstValueFrom, of, EMPTY } from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { LocalStorageService } from 'angular-2-local-storage';
import { NotificationsService } from '@pn-plus/pnp-services';
import { catchError } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { LoginService } from '../../core/services/login.service';
import { AuthErrors, AuthErrorService, AuthService, PnpLoginService, PnpUserService } from '@pn-plus/pnp-services';
import { ILoginPayload, ILoginResponse } from 'libs/pnp-services/src/lib/api/login/pnp-login.model';
import { IRegistrationResponse, IUserPhotoResponse, IUserResponse } from 'libs/pnp-services/src/lib/api/user/pnp-user.model';
import { IAuthModel } from 'libs/pnp-services/src/lib/auth/_models/auth.model';
import { VerifyTwofactorPhoneComponent } from '../verify-twofactor-phone/verify-twofactor-phone.component';
import { FingerprintService } from '../../core/services/fingerprint.service';

import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '@pn-plus/pnp-environment';

@Component({
    selector: 'nrwl-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent implements OnInit, OnDestroy {
    @ViewChild('cbRememberMe', { read: ElementRef, static: false }) cbRememberMeRef: ElementRef<HTMLElement>;
    @ViewChild('aForgotPassword', { read: ElementRef, static: false }) aForgotPasswordRef: ElementRef<HTMLElement>;
    @ViewChild('btSubmit', { read: ElementRef, static: false }) btSubmit: ElementRef<HTMLElement>;
    @ViewChild('iPractice', { read: ElementRef, static: false }) iPractice: ElementRef<HTMLElement>;

    public loginForm: FormGroup;
    public showPassword = false;
    public isLoading = false;
    activeError: any;

    public isPracticeError = false;
    public isUserNamePasswordError = false;

    private unsubscriber : Subject<void> = new Subject<void>();

    constructor(
        private formBuilder: FormBuilder,
        private authService: AuthService,
        public dialog: MatDialog,
        public loginService: LoginService,
        public authErrorService: AuthErrorService,
        private pnpLoginService: PnpLoginService,
        private pnpUserService: PnpUserService,
        private localStorage: LocalStorageService,
        private fingerprintService: FingerprintService,
        private notificationsService: NotificationsService,
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.showErrorSnackbar();
        this.loginService.setInvitationProcess(false);

        this.loginService.isRecentDialogOpened
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((isOpened: boolean) => {
                if(isOpened) {
                    this.resetForm();
                }
            });

        this.loginService.autoLoginObservable
            .pipe(
                takeUntil(this.unsubscriber),
            ).subscribe(async (loginData: any) => {
                if(loginData) {
                    this.loginService.autoLoginObservable.next(null);
                    this.initForm(loginData);
                    await this.onSubmit();
                }
            });

        history.pushState(null, '', environment.pnLoginUrl);
        fromEvent(window, 'popstate').pipe(
            takeUntil(this.unsubscriber),
        ).subscribe((_) => {
            history.pushState(null, '', environment.pnLoginUrl);
        });
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }

    initForm(initial: any = null) {
        this.loginForm = this.formBuilder.group(
            {   
                practice: [initial?.practice || '', Validators.required],
                user: [initial?.user || '', Validators.required],
                password: [initial?.password || '', Validators.required],
                remember_me: [false],
                deviceId: [`${this.fingerprintService.getFingerPrint()}`],
                timezone: [Intl.DateTimeFormat().resolvedOptions().timeZone],
            },
        );
    }

    resetForm() {
        this.initForm();
    }

    public showErrorSnackbar(): void {
        const errorSnackbar: string = this.localStorage.get('showErrorSnackbar');
        if (!errorSnackbar) return;

        this.notificationsService.showWarningNotification(errorSnackbar);
        this.localStorage.remove('showErrorSnackbar');
    }

    async onSubmit() {
        let authModel: IAuthModel;
        if (this.loginForm.valid) {
            this.localStorage.remove(environment.localStorage.inactivityTimestamp);
            this.setBusy(true);
            authModel = this.initAuthModel(this.loginForm.value);
            authModel = await firstValueFrom(this.tryLogin(authModel, this.loginForm.value));

            const accessToken = authModel.loginResponse?.accessToken;
            const twoFactorToken = authModel.loginResponse?.twoFactorToken;

            if(accessToken) {
                authModel = await this.processRegistration(authModel);
            } else if(twoFactorToken) {
                authModel = await this.process2FA(authModel);
            }

            if (authModel.error) {
                const activeError = this.authErrorService.getErrorByType(authModel.error.type);
                if (!this.setSpecificErrorType(activeError)) {
                    if(activeError === AuthErrors.UserLocked) {
                        this.activeError = authModel.error;
                    } else {
                        this.activeError = activeError;
                    }    
                }
            }

            this.setBusy(false);
        }
    }

    async process2FA(authModel: IAuthModel) {
        this.setBusy(false);

        authModel.error = AuthErrors.Success;
        this.authService.updateAuthModel(authModel);

        if (authModel?.loginResponse?.twoFactorToken?.userPhoneNumberLastFour) {
            this.loginService.navigate('/verify-twofactor-phone');
            //await this.openTwoFactorDialog();
        } else {
            this.loginService.navigate('/set-phone');
        }

        return authModel;
    }

    openTwoFactorDialog(): Promise<any> {
        return new Promise(async (resolve, reject) => {
            const authModel = this.authService.getAuthModel();
            return this.dialog.open(VerifyTwofactorPhoneComponent, {
                    data: authModel.loginResponse?.twoFactorToken,
                    disableClose: false,
                    panelClass: 'reset-password-dialog',
                    width: '100%',
                    maxWidth: '396px',
                })
                .afterClosed()
                .subscribe((result) => {
                    this.resetForm();
                    resolve(result);
                });
        });
    }



    public async processRegistration(authModel: IAuthModel) {
        authModel = await firstValueFrom(this.loadUserRegistration(authModel));
        authModel = await firstValueFrom(this.loadUser(authModel));
        authModel = await firstValueFrom(this.loadUserPhoto(authModel));
        authModel = await this.loginService.storeBase64Avatar(authModel);

        authModel.error = AuthErrors.Success;
        this.authService.updateAuthModel(authModel);

        // Process 2FA when AccessToken present
        if(authModel.loginResponse?.twoFactorToken) {
            if(!authModel.userResponse?.phoneNumber) {
                this.loginService.navigate('/set-phone');
            } else if(!authModel.userResponse?.phoneNumberConfirmed) {
                this.loginService.navigate('/verify-recovery-phone');
            }
        } else {
            this.loginService.navigateRegistration(authModel);
        }
        return authModel;
    }

    public initAuthModel(loginPayload: ILoginPayload): IAuthModel {
        this.authService.resetLogin();
        const authModel: IAuthModel = {
            loginPayload: loginPayload,
            error: null,
        };
        return authModel;
    }

    private tryLogin(authModel: IAuthModel, loginPayload: ILoginPayload): Observable<any> {
        return this.pnpLoginService.postLogin(loginPayload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authModel.error = errorResponse.error;
                    return of(null);
                }),
                map((loginResponse?: ILoginResponse | null) => {
                    if (loginResponse) {
                        authModel.loginResponse = loginResponse;
                        this.authService.updateAuthModel(authModel);
                    }
                    return authModel;
                }),
            );
    }

    private loadUserRegistration(authModel: IAuthModel) {
        if (authModel.error) {
            return of(authModel);
        }

        return this.pnpUserService.getUserRegistration()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authModel.error = errorResponse.error;
                    return of(null);
                }),
                map((registrationResponse: IRegistrationResponse | null) => {
                    if(registrationResponse) {
                        authModel.registrationResponse = registrationResponse;
                    }
                    return authModel;
                }),
            );
    }

    private loadUser(authModel: IAuthModel) {
        if (authModel.error) {
            return of(authModel);
        }

        return this.pnpUserService.getUser()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authModel.error = errorResponse.error;
                    return of(null);
                }),
                map((userResponse: IUserResponse | null) => {
                    if(userResponse) {
                        authModel.userResponse = userResponse;
                    }
                    return authModel;
                }),
            );
    }

    private loadUserPhoto(authModel: IAuthModel) {
        if (authModel.error) {
            return of(authModel);
        }

        return this.pnpUserService.getUserPhoto()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authModel.error = errorResponse.error;
                    return of(null);
                }),
                map((userPhotoResponse: IUserPhotoResponse | null) => {
                    if(userPhotoResponse){
                        authModel.userPhotoResponse = userPhotoResponse;
                    }
                    return authModel;
                }),
            );
    }

    private getTimeZone() {
        const offset = new Date().getTimezoneOffset(), o = Math.abs(offset);
        return (offset < 0 ? '+' : '-') + ('00' + Math.floor(o / 60)).slice(-2) + ':' + ('00' + (o % 60)).slice(-2);
    }

    public randomNumbers(min: number, max: number) {
        return Math.round(Math.random() * (max - min)) + min;
    }

    toggleShowPassword() {
        this.showPassword = !this.showPassword;
    }

    setBusy(flag: boolean): void {
        this.isLoading = flag;
    }

    async onForgotPasswordClicked($event: any) {
        let response = null;
        $event.preventDefault();
        $event.stopPropagation();

        response = await this.loginService.processForgotPassword();
        if(response === AuthErrors.TokenExpired){
            this.activeError = response;
        }
    }

    keydownPasswordEvent($event: any) {
        if (!$event.shiftKey && $event.key === 'Tab' && !this.loginForm.valid) {
            $event.preventDefault();
            $event.stopPropagation();
            this.cbRememberMeRef.nativeElement.focus();
        }
        else if (!$event.shiftKey && $event.key === 'Tab'){
            $event.preventDefault();
            $event.stopPropagation();
            this.btSubmit.nativeElement.focus();
        }
    }

    keydownEvent($event: any) {
        if($event.code === 'Tab') {
            $event.preventDefault();
            $event.stopPropagation();
            this.cbRememberMeRef.nativeElement.focus();
        }
    }

    keydownPractice($event: any) {
        if ($event.shiftKey && $event.key === 'Tab' && this.loginForm.valid) {
            $event.preventDefault();
            $event.stopPropagation();
            this.btSubmit.nativeElement.focus();
        }
    }

    private setSpecificErrorType(activeError: any) : boolean {
        this.isPracticeError = activeError === AuthErrors.PracticeNotValid;
        this.isUserNamePasswordError = activeError === AuthErrors.UserOrPasswordNotValid;

        if (this.isPracticeError || this.isUserNamePasswordError) {
            return true;
        } else {
            return false;
        }
    }
}
